<script lang="ts">
    import { writable } from 'svelte/store'

    import { isEqual } from 'lodash-es'

    import type { CrumbsWritableStore } from 'hydria/components/breadcrumb'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import type { INotification } from 'hydria/services/notifications'
    import { session, simpleFetch } from 'hydria/services/session'

    import { Breadcrumb } from 'breadcrumb'

    // General: User’s name, communication lang

    const crumbs: CrumbsWritableStore = writable([
        {
            txt: tgettext('Utilisateur'),
        },
        {
            txt: tgettext('Informations générales'),
        },
    ])
    let loading = $state(true)
    let saving = $state(false)
    let error = false

    interface UserEdit {
        name: string
        prefered_lg: 'fr' | 'en' | undefined
        force_prefered_lg: boolean
    }
    let orig: UserEdit = $state({
        name: '',
        prefered_lg: undefined,
        force_prefered_lg: true,
    })
    let edit: UserEdit = $state({ ...orig })

    async function loadData() {
        try {
            const r = await simpleFetch('GET', '/json/session/1/user')

            orig = {
                name: r.data.metadata.name,
                prefered_lg: r.data.metadata.prefered_lg,
                force_prefered_lg: r.data.metadata.force_prefered_lg,
            }
            edit = { ...orig }
            if (edit.prefered_lg == null) edit.prefered_lg = 'fr'
            crumbs.update((v) => {
                v[0] = {
                    txt: r.data.metadata.name,
                }
                return v
            })
        } catch {
            notify({
                type: 'danger',
                title: $_('Information de l’utilisateur'),
                text: $_('L’information de l’utilisateur n’a pas pu être chargée.'),
            })
            error = true
        }
        loading = false
    }
    loadData()

    let dirty = $derived(!isEqual(orig, edit))
    let nameValid = $derived(edit.name && edit.name.length > 1)
    let lgValid = $derived(!!edit.prefered_lg)
    let valid = $derived(nameValid && lgValid)

    async function saveData() {
        if (!dirty || loading || saving) return

        saving = true
        error = false

        const notifyError: INotification = {
            type: 'danger',
            title: $_('Information de l’utilisateur'),
            text: $_('L’information de l’utilisateur n’a pas pu être sauvegardée.'),
        }
        try {
            await simpleFetch('PUT', '/json/session/1/user', {
                name: edit.name,
                prefered_lg: edit.prefered_lg,
                force_prefered_lg: edit.force_prefered_lg,
            })
            notify({
                type: 'primary',
                title: $_('Information de l’utilisateur'),
                text: $_('L’information de l’utilisateur a été sauvegardée.'),
            })
            orig = { ...edit }
            crumbs.update((v) => {
                v[0] = {
                    txt: orig.name,
                }
                return v
            })
            session.validateCurrentSession(true)
        } catch (e) {
            console.error('Failed to save user information:', e)
            error = true
            notify(notifyError)
        }
        saving = false
    }
</script>

<Breadcrumb {crumbs} />

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <form
        onsubmit={(e) => {
            e.preventDefault()
            saveData()
        }}
    >
        <div class="card border-group">
            <div class="card-header text-bg-primary">{$_('WebDomaine account information')}</div>
            <div class="card-body d-grid gap-2" style="max-width: 800px;">
                <div class="row">
                    <label for="name" class="col-sm-3 col-form-label">{$_('Full name')}</label>
                    <div class="col-sm-8 col-md-9 col-lg-7">
                        <input type="text" class="form-control" id="name" bind:value={edit.name} class:is-invalid={!nameValid} />
                        <span class="form-text">{$_('Used as billing information and in emails')}</span>
                    </div>
                </div>
                <div class="row align-items-baseline">
                    <label for="lg" class="col-sm-3 col-form-label">{$_('Your preferred language of communication')}</label>
                    <div class="col-sm-8 col-md-8 col-lg-7">
                        <div class="px-0 form-check">
                            <input type="radio" value="fr" bind:group={edit.prefered_lg} id="u-lg-fr" />
                            <label for="u-lg-fr" class="radio radio-inline" class:text-danger={!lgValid}>{$_('French')}</label>
                        </div>
                        <div class="px-0 form-check">
                            <input type="radio" value="en" bind:group={edit.prefered_lg} id="u-lg-en" />
                            <label for="u-lg-en" class="radio radio-inline" class:text-danger={!lgValid}>{$_('English')}</label>
                        </div>
                        <span class="form-text">{$_('Determines the language of the emails we send you')}</span>
                    </div>
                </div>
                <div class="row align-items-baseline">
                    <label for="force_prefered_lg" class="col-sm-3 col-form-label">{$_('Langue de connexion')}</label>
                    <div class="col-sm-8 col-md-8 col-lg-7">
                        <div class="px-0 form-check">
                            <input type="checkbox" class="form-check-input" id="force_prefered_lg" bind:checked={edit.force_prefered_lg} />
                            <label for="force_prefered_lg" class="form-check-label">{$_('Forcer l’utilisation de cette langue lors de la connexion')}</label>
                        </div>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="offset-sm-3 col-sm-8 col-md-9 col-lg-7">
                        {#if dirty}
                            <button
                                type="button"
                                class="btn btn-warning mobile-100 margin-right-btn"
                                onclick={() => {
                                    edit = { ...orig }
                                }}>{$_('Revert changes')}</button
                            >
                        {/if}
                        <button type="submit" class="btn btn-primary mobile-100" disabled={saving || !dirty || !valid}>
                            {#if saving}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {$_('Saving…')}
                            {:else}{$_('Submit changes')}
                            {/if}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </form>
{/if}
